import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Save, Trash2, X, Loader2, Download, Send, MessageCircle, FileText, Search, Users, Percent, Link2, Check, Copy, IndianRupee, Calendar } from 'lucide-react';
import { API, adminAuthHeader } from '../api';
import AdminSearch from './AdminSearch';
import { fmtDate } from '../utils/dateFormat';

// Whole-number rupee formatting — no decimals anywhere in the admin UI or PDFs.
const money = (v) => `INR ${Math.round(Number(v || 0)).toLocaleString('en-IN')}`;
const moneyInt = (v) => Math.round(Number(v || 0)).toLocaleString('en-IN');

const emptyCustomer = { name: '', address: '', city: '', state: '', state_code: '', pincode: '', phone: '', email: '', gstin: '' };
const emptyItem = () => ({ description: '', subtitle: '', hsn_sac: '9985', quantity: 1, rate: 0, gst_rate: 18 });

const PAYMENT_METHODS = ['UPI', 'Bank Transfer', 'Card', 'Cash', 'Cheque', 'Other'];

const PaymentsBlock = ({ form, addPayment, removePayment, refresh }) => {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('UPI');
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const payments = form._payments || [];
  const totalPaid = Number(form._total_paid || 0);
  const balance = Number(form._balance_due || 0);
  const grand = Number(form._grand_total || 0);
  const status = form._payment_status || 'unpaid';

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) { setErr('Enter an amount greater than 0'); return; }
    setErr('');
    setBusy(true);
    try {
      await addPayment(form, { amount: amt, method, reference, date, notes });
      setAmount(''); setReference(''); setNotes('');
      await refresh();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Failed to record payment');
    } finally { setBusy(false); }
  };

  const del = async (pid) => {
    if (!window.confirm('Delete this payment record?')) return;
    setBusy(true);
    try {
      await removePayment(form._id, pid);
      await refresh();
    } finally { setBusy(false); }
  };

  // Payment status per spec: Fully Paid (green) / Unpaid (red) / Balance Due (red)
  const isFullyPaid = grand > 0 && totalPaid >= grand;
  const isUnpaid = totalPaid <= 0;
  const statusMeta = isFullyPaid
    ? { label: 'Fully Paid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
    : isUnpaid
      ? { label: 'Unpaid', cls: 'bg-rose-50 text-rose-700 border-rose-200' }
      : { label: `Balance Due: ₹${balance.toLocaleString('en-IN')}`, cls: 'bg-rose-50 text-rose-700 border-rose-200' };

  // Warn when travel start date is within 15 days AND payment is not fully paid.
  let dueSoon = false;
  const ts = ((form.travel_start_date || form._travel_start_date) || '').slice(0, 10);
  if (!isFullyPaid && ts) {
    const t = new Date(ts + 'T00:00:00');
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const diffDays = Math.round((t - now) / 86400000);
    if (diffDays >= 0 && diffDays <= 15) dueSoon = true;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4" data-testid="payments-block">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <IndianRupee size={16} className="text-[#0d64a8]" />
          <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-widest">Payments</h3>
          <span data-testid="payments-status" className={`text-[11px] font-semibold border rounded-full px-2.5 py-0.5 ${statusMeta.cls}`}>{statusMeta.label}</span>
          {dueSoon && (
            <span data-testid="payments-due-warning" className="text-[11px] font-medium border rounded-full px-2.5 py-0.5 bg-rose-50 text-rose-700 border-rose-300">
              Full payment due — travel date is within 15 days.
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500">
          Paid <strong className="text-emerald-700">{`INR ${totalPaid.toLocaleString('en-IN')}`}</strong>
          {' · '}
          Balance <strong className={balance <= 0 ? 'text-emerald-700' : 'text-rose-600'}>{`INR ${balance.toLocaleString('en-IN')}`}</strong>
          {' · Total '} <strong>{`INR ${grand.toLocaleString('en-IN')}`}</strong>
        </div>
      </div>

      {/* Add payment row */}
      <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end border border-dashed border-gray-200 rounded-md p-3 bg-gray-50/60" data-testid="add-payment-form">
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:border-[#0d64a8] bg-white" data-testid="pay-date"/>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Amount (INR)</label>
          <input type="number" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:border-[#0d64a8] bg-white" data-testid="pay-amount"/>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Method</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:border-[#0d64a8] bg-white" data-testid="pay-method">
            {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Reference (UTR / cheque no)</label>
          <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:border-[#0d64a8] bg-white" data-testid="pay-ref"/>
        </div>
        <div>
          <button type="submit" disabled={busy} data-testid="pay-add-btn" className="w-full bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-3 py-2 rounded-md disabled:opacity-60 flex items-center justify-center gap-1">
            {busy ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13}/>} Record
          </button>
        </div>
      </form>
      {err && <div className="mt-2 text-xs text-red-600">{err}</div>}

      {/* Payment history table */}
      {payments.length === 0 ? (
        <div className="mt-3 text-center py-6 text-xs text-gray-400 border border-dashed border-gray-200 rounded-md">No payments recorded yet.</div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-[11px] uppercase tracking-wider bg-gray-50">
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Method</th>
                <th className="text-left px-3 py-2">Reference</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="px-3 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-gray-100" data-testid={`payment-row-${p.id}`}>
                  <td className="px-3 py-2 text-gray-700 flex items-center gap-1.5"><Calendar size={12} className="text-gray-400"/> {fmtDate(p.date) || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{p.method}</td>
                  <td className="px-3 py-2 text-gray-500 font-mono text-xs">{p.reference || '—'}</td>
                  <td className="px-3 py-2 text-right font-semibold text-emerald-700">{`INR ${Number(p.amount || 0).toLocaleString('en-IN')}`}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => del(p.id)} className="text-gray-400 hover:text-red-500" data-testid={`payment-del-${p.id}`}><Trash2 size={13}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const InvoicesManager = () => {
  const [invoices, setInvoices] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [states, setStates] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [enquirySearch, setEnquirySearch] = useState('');
  const [showEnquiryDropdown, setShowEnquiryDropdown] = useState(false);
  const [copiedFor, setCopiedFor] = useState('');
  const [sharePanel, setSharePanel] = useState(null); // invoice being shared

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [inv, st, enq] = await Promise.all([
        axios.get(`${API}/admin/invoices`, { headers: adminAuthHeader() }),
        axios.get(`${API}/admin/india-states`, { headers: adminAuthHeader() }),
        axios.get(`${API}/admin/enquiries`, { headers: adminAuthHeader() }).catch(() => ({ data: [] })),
      ]);
      setInvoices(inv.data || []);
      setStates(st.data || []);
      setEnquiries(enq.data || []);
    } finally { setLoading(false); }
  };

  const startNew = () => {
    setForm({
      invoice_date: new Date().toISOString().slice(0, 10),
      due_date: '',
      customer: { ...emptyCustomer },
      items: [emptyItem()],
      notes: '',
      status: 'draft',
      show_tax_breakdown: false,
      commission_amount: 0,
      is_international: false,
      apply_tcs: false,
      tcs_rate: 2,
      package_ref: '',
    });
    setEditing('new');
    setEnquirySearch('');
  };

  const startEdit = async (inv) => {
    const res = await axios.get(`${API}/admin/invoices/${inv.id}`, { headers: adminAuthHeader() });
    const d = res.data;
    setForm({
      invoice_date: (d.invoice_date || '').slice(0, 10),
      due_date: (d.due_date || '').slice(0, 10),
      customer: { ...emptyCustomer, ...(d.customer || {}) },
      items: (d.items && d.items.length) ? d.items.map((it) => ({ ...emptyItem(), ...it })) : [emptyItem()],
      notes: d.notes || '',
      status: d.status || 'draft',
      show_tax_breakdown: !!d.show_tax_breakdown,
      commission_amount: d.commission_amount || 0,
      is_international: !!d.is_international,
      apply_tcs: !!d.apply_tcs,
      tcs_rate: d.tcs_rate || 2,
      package_ref: d.package_ref || '',
      travel_start_date: (d.travel_start_date || '').slice(0, 10),
      travel_end_date: (d.travel_end_date || '').slice(0, 10),
      _id: d.id,
      _invoice_number: d.invoice_number,
      _public_token: d.public_token,
      _payments: d.payments || [],
      _total_paid: d.total_paid || 0,
      _balance_due: d.balance_due || 0,
      _grand_total: d.grand_total || 0,
      _payment_status: d.payment_status || 'unpaid',
    });
    setEditing(d.id);
  };

  const refreshCurrent = async () => {
    if (!form?._id) return;
    const res = await axios.get(`${API}/admin/invoices/${form._id}`, { headers: adminAuthHeader() });
    const d = res.data;
    setForm((f) => ({
      ...f,
      _payments: d.payments || [],
      _total_paid: d.total_paid || 0,
      _balance_due: d.balance_due || 0,
      _grand_total: d.grand_total || 0,
      _payment_status: d.payment_status || 'unpaid',
    }));
  };

  const cancel = () => { setEditing(null); setForm(null); };

  const setCustomerField = (k, v) => setForm((f) => ({ ...f, customer: { ...f.customer, [k]: v } }));
  const setCustomerState = (name) => {
    const st = states.find((x) => x.name === name);
    setForm((f) => ({ ...f, customer: { ...f.customer, state: name, state_code: st?.code || '' } }));
  };

  const pickEnquiry = (e) => {
    setForm((f) => ({
      ...f,
      customer: {
        ...f.customer,
        name: e.client_name || f.customer.name,
        phone: e.phone || f.customer.phone,
        email: e.email || f.customer.email,
      },
      package_ref: e.package_ref || f.package_ref,
      notes: f.notes || (e.query ? `From enquiry: ${e.query}` : ''),
    }));
    setEnquirySearch('');
    setShowEnquiryDropdown(false);
  };

  const filteredEnquiries = useMemo(() => {
    const q = enquirySearch.trim().toLowerCase();
    if (!q) return enquiries.slice(0, 8);
    return enquiries.filter((e) =>
      (e.client_name || '').toLowerCase().includes(q) ||
      (e.phone || '').toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q)
    ).slice(0, 10);
  }, [enquiries, enquirySearch]);

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const rmItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const setItem = (idx, k, v) => setForm((f) => ({
    ...f,
    items: f.items.map((it, i) => i === idx ? { ...it, [k]: v } : it),
  }));

  // Live client-side totals preview — mirrors backend _compute_invoice_totals
  const totals = useMemo(() => {
    if (!form) return null;
    let subtotal = 0;
    form.items.forEach((it) => { subtotal += (Number(it.quantity) || 0) * (Number(it.rate) || 0); });
    const commission = Number(form.commission_amount) || 0;
    const commission_gst = Math.round(commission * 0.18 * 100) / 100;
    const customer_package_cost = Math.round((subtotal + commission + commission_gst) * 100) / 100;
    const tcs_rate = Number(form.tcs_rate) || 2;
    const tcs_amount = (form.is_international && form.apply_tcs)
      ? Math.round(customer_package_cost * tcs_rate) / 100
      : 0;
    const grand_total = Math.round(customer_package_cost + tcs_amount);
    return {
      subtotal,
      commission,
      commission_gst,
      customer_package_cost,
      tcs_amount,
      tcs_rate,
      grand_total,
    };
  }, [form]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        invoice_date: form.invoice_date || undefined,
        due_date: form.due_date || null,
        customer: form.customer,
        items: form.items.map((it) => ({
          description: it.description,
          subtitle: it.subtitle,
          hsn_sac: it.hsn_sac || '9985',
          quantity: Number(it.quantity) || 0,
          rate: Number(it.rate) || 0,
          gst_rate: Number(it.gst_rate) || 18,
        })),
        notes: form.notes,
        status: form.status,
        show_tax_breakdown: !!form.show_tax_breakdown,
        commission_amount: Number(form.commission_amount) || 0,
        is_international: !!form.is_international,
        apply_tcs: !!form.apply_tcs,
        tcs_rate: Number(form.tcs_rate) || 2,
        package_ref: form.package_ref || null,
      };
      if (editing === 'new') {
        await axios.post(`${API}/admin/invoices`, payload, { headers: adminAuthHeader() });
      } else {
        await axios.put(`${API}/admin/invoices/${editing}`, payload, { headers: adminAuthHeader() });
      }
      await load();
      cancel();
    } finally { setSaving(false); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    await axios.delete(`${API}/admin/invoices/${inv.id}`, { headers: adminAuthHeader() });
    await load();
  };

  const duplicate = async (inv) => {
    if (!window.confirm(`Duplicate invoice ${inv.invoice_number}? A new draft will be created with today's date.`)) return;
    const res = await axios.post(`${API}/admin/invoices/${inv.id}/duplicate`, {}, { headers: adminAuthHeader() });
    await load();
    // Open the new duplicate for editing
    startEdit(res.data);
  };

  const addPayment = async (inv, payload) => {
    const res = await axios.post(`${API}/admin/invoices/${inv._id}/payments`, payload, { headers: adminAuthHeader() });
    return res.data;
  };

  const removePayment = async (invId, paymentId) => {
    const res = await axios.delete(`${API}/admin/invoices/${invId}/payments/${paymentId}`, { headers: adminAuthHeader() });
    return res.data;
  };

  const downloadPdf = async (inv, mode = 'customer') => {
    try {
      const res = await axios.get(`${API}/admin/invoices/${inv.id}/pdf`, {
        headers: adminAuthHeader(),
        params: { mode },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const suffix = mode === 'accountant' ? '-Accountant' : '';
      a.download = `Invoice-${(inv.invoice_number || '').replace(/\//g, '-')}${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 200);
    } catch (e) { window.alert('Download failed'); }
  };

  const publicUrl = (inv) => {
    if (!inv || !inv.invoice_number) return '';
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const enc = encodeURIComponent(inv.invoice_number);
    return `${backend}/api/invoices/public/${enc}?token=${inv.public_token}`;
  };

  const shareWhatsApp = (inv) => {
    const url = publicUrl(inv);
    const name = inv.customer?.name || 'there';
    const text = `Hi ${name}, please find your invoice ${inv.invoice_number} for ${money(inv.grand_total)}. Download PDF: ${url}`;
    window.open(`https://wa.me/${(inv.customer?.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareEmail = (inv) => {
    const url = publicUrl(inv);
    const subject = `Invoice ${inv.invoice_number} from Travelsera`;
    const body = `Hi ${inv.customer?.name || 'there'},\n\nPlease find attached your invoice ${inv.invoice_number} for a total of ${money(inv.grand_total)}.\n\nDownload / view the PDF here:\n${url}\n\nAmount in words: ${inv.amount_in_words}\n\nRegards,\nTravelsera`;
    const to = inv.customer?.email || '';
    window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const copyPublicLink = async (inv) => {
    const url = publicUrl(inv);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedFor(inv.id);
      setTimeout(() => setCopiedFor((c) => c === inv.id ? '' : c), 1600);
    } catch (_) { window.prompt('Copy this link:', url); }
  };

  // ------------------ RENDER ------------------
  if (editing !== null && form) {
    return (
      <div data-testid="invoice-editor">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{editing === 'new' ? 'New Invoice' : `Editing invoice`}</h2>
            <p className="text-xs text-gray-500">Fill customer details, add line items, and save. GST computes automatically on commission (18%) — hidden from the customer PDF by default.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={cancel} className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-200 rounded-md"><X size={14} className="inline mr-1"/> Cancel</button>
            <button onClick={save} disabled={saving} data-testid="invoice-save-btn" className="text-sm bg-[#0d64a8] hover:bg-[#0a548e] text-white px-4 py-1.5 rounded-md disabled:opacity-60 flex items-center gap-1.5">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} {saving ? 'Saving…' : 'Save Invoice'}
            </button>
          </div>
        </div>

        {/* Meta row */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Invoice Date *</label>
            <input type="date" value={form.invoice_date || ''} onChange={(e) => setForm((f) => ({ ...f, invoice_date: e.target.value }))} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" data-testid="inv-date"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Due Date <span className="text-gray-400">(optional)</span></label>
            <input type="date" value={form.due_date || ''} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] bg-white" data-testid="inv-status">
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700 select-none cursor-pointer">
              <input type="checkbox" checked={!!form.show_tax_breakdown} onChange={(e) => setForm((f) => ({ ...f, show_tax_breakdown: e.target.checked }))} className="accent-[#0d64a8]" data-testid="inv-show-tax"/>
              Show GST breakdown on PDF
            </label>
          </div>
          <div className="col-span-2 md:col-span-4 -mt-1">
            <p className="text-[11px] text-gray-500">
              <strong>GST breakdown OFF (default):</strong> Customer PDF shows a clean bill with all-inclusive amounts. 18% GST on commission is captured internally for your records. <strong>ON:</strong> Full India GST tax invoice with CGST/SGST or IGST columns.
            </p>
          </div>
        </div>

        {/* Customer */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-widest">Bill To (Customer)</h3>
            <div className="relative">
              <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5 focus-within:border-[#0d64a8]">
                <Search size={13} className="text-gray-400"/>
                <input
                  type="text"
                  placeholder="Fill from past enquiry…"
                  value={enquirySearch}
                  onChange={(e) => { setEnquirySearch(e.target.value); setShowEnquiryDropdown(true); }}
                  onFocus={() => setShowEnquiryDropdown(true)}
                  className="bg-transparent text-xs outline-none w-56"
                  data-testid="enquiry-search"
                />
                <Users size={13} className="text-gray-400"/>
              </div>
              {showEnquiryDropdown && filteredEnquiries.length > 0 && (
                <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-20 max-h-72 overflow-auto" onMouseLeave={() => setShowEnquiryDropdown(false)}>
                  {filteredEnquiries.map((e) => (
                    <button key={e.id} type="button" onClick={() => pickEnquiry(e)} data-testid={`enquiry-pick-${e.id}`} className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0">
                      <div className="text-sm font-medium text-gray-900">{e.client_name}</div>
                      <div className="text-[11px] text-gray-500">{e.phone} · {e.email || '—'}</div>
                      {e.query && <div className="text-[11px] text-gray-400 line-clamp-1">{e.query}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Customer Name *</label>
              <input value={form.customer.name} onChange={(e) => setCustomerField('name', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" data-testid="cust-name"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">GSTIN (optional)</label>
              <input value={form.customer.gstin} onChange={(e) => setCustomerField('gstin', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
              <input value={form.customer.address} onChange={(e) => setCustomerField('address', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
              <input value={form.customer.city} onChange={(e) => setCustomerField('city', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
              <select value={form.customer.state || ''} onChange={(e) => setCustomerState(e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] bg-white" data-testid="cust-state">
                <option value="">Select state</option>
                {states.map((s) => <option key={s.code} value={s.name}>{s.name} ({s.code})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Pincode</label>
              <input value={form.customer.pincode} onChange={(e) => setCustomerField('pincode', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
              <input value={form.customer.phone} onChange={(e) => setCustomerField('phone', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" data-testid="cust-phone"/>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input type="email" value={form.customer.email} onChange={(e) => setCustomerField('email', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" data-testid="cust-email"/>
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-widest">Line Items</h3>
            <button onClick={addItem} data-testid="item-add" className="text-xs bg-gray-50 border border-gray-200 rounded-md px-3 py-1.5 hover:border-[#0d64a8] hover:text-[#0d64a8] flex items-center gap-1"><Plus size={12}/> Add row</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-[11px] uppercase tracking-wider">
                  <th className="px-2 py-2 text-left w-8">#</th>
                  <th className="px-2 py-2 text-left">Description</th>
                  <th className="px-2 py-2 text-left w-24">HSN/SAC</th>
                  <th className="px-2 py-2 text-right w-20">Qty</th>
                  <th className="px-2 py-2 text-right w-28">Rate (INR)</th>
                  <th className="px-2 py-2 text-right w-28">Amount</th>
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => {
                  const line = (Number(it.quantity) || 0) * (Number(it.rate) || 0);
                  return (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-2 py-2 text-gray-400">{i + 1}</td>
                      <td className="px-2 py-2">
                        <input value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} placeholder="e.g. Himalayan Bliss Premium Package" className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:border-[#0d64a8]" data-testid={`item-desc-${i}`}/>
                        <input value={it.subtitle || ''} onChange={(e) => setItem(i, 'subtitle', e.target.value)} placeholder="Extra detail (e.g. 7N/8D · 2 adults)" className="w-full border border-gray-100 rounded-md px-2 py-1 text-[12px] mt-1 focus:border-[#0d64a8] text-gray-600"/>
                      </td>
                      <td className="px-2 py-2">
                        <input value={it.hsn_sac || ''} onChange={(e) => setItem(i, 'hsn_sac', e.target.value)} className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:border-[#0d64a8]"/>
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="1" value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm text-right focus:border-[#0d64a8]"/>
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="1" value={it.rate} onChange={(e) => setItem(i, 'rate', e.target.value)} className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm text-right focus:border-[#0d64a8]" data-testid={`item-rate-${i}`}/>
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-gray-900">{money(line)}</td>
                      <td className="px-2 py-2 text-right">
                        {form.items.length > 1 && (
                          <button type="button" onClick={() => rmItem(i)} className="text-gray-400 hover:text-red-500" data-testid={`item-rm-${i}`}><Trash2 size={14}/></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes + commission + totals */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <label className="block text-xs font-semibold text-gray-800 uppercase tracking-widest mb-2">Notes to customer (optional)</label>
              <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={4} placeholder="e.g. Package includes hotels, meals, transfers. 50% advance received." className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none"/>
            </div>
            <div className="bg-amber-50/40 border border-amber-100 rounded-lg p-4">
              <label className="block text-xs font-semibold text-amber-900 uppercase tracking-widest mb-1">Commission (Service Fee)</label>
              <p className="text-[11px] text-amber-800/80 mb-2">Auto-synced from the linked Itinerary — edit it in the Itinerary Builder → Pricing panel. 18% GST is auto-computed on this figure. <strong>Formula:</strong> Grand Total = Package Cost + Commission + 18% × Commission + 2% TCS (if international).</p>
              <div className="flex items-center gap-2">
                <Percent size={14} className="text-amber-700"/>
                <input type="number" step="1" value={form.commission_amount} readOnly disabled placeholder="0" className="w-40 border border-amber-200 rounded-md px-3 py-2 text-sm bg-amber-100/60 text-gray-700 cursor-not-allowed" data-testid="commission-input" title="Read-only: this value is synced from the Itinerary."/>
                <span className="text-xs text-amber-800">to GST @ 18% = <strong>{money(Number(form.commission_amount || 0) * 0.18)}</strong></span>
              </div>
            </div>

            {/* International travel + TCS 206C(1G) */}
            <div className="bg-sky-50/50 border border-sky-100 rounded-lg p-4" data-testid="tcs-panel">
              <label className="block text-xs font-semibold text-sky-900 uppercase tracking-widest mb-1">International Travel · TCS</label>
              <p className="text-[11px] text-sky-800/80 mb-3">Turn on for international packages. When both toggles are on, {form.tcs_rate || 2}% TCS is collected on the invoice total (section 206C(1G)) and shown as a separate line on both customer & accountant copies.</p>
              <div className="flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-sky-900 cursor-pointer" data-testid="toggle-international">
                  <input
                    type="checkbox"
                    checked={!!form.is_international}
                    onChange={(e) => setForm((f) => ({ ...f, is_international: e.target.checked, apply_tcs: e.target.checked ? f.apply_tcs : false }))}
                    className="accent-[#0d64a8]"
                  />
                  This is an international trip
                </label>
                <label className={`inline-flex items-center gap-2 text-sm ${form.is_international ? 'text-sky-900 cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`} data-testid="toggle-tcs">
                  <input
                    type="checkbox"
                    disabled={!form.is_international}
                    checked={!!form.apply_tcs}
                    onChange={(e) => setForm((f) => ({ ...f, apply_tcs: e.target.checked }))}
                    className="accent-[#0d64a8]"
                  />
                  Apply {form.tcs_rate || 2}% TCS
                </label>
                <div className="inline-flex items-center gap-2 text-xs text-sky-900">
                  <span>TCS rate</span>
                  <input
                    type="number"
                    step="1"
                    disabled={!form.is_international}
                    value={form.tcs_rate}
                    onChange={(e) => setForm((f) => ({ ...f, tcs_rate: e.target.value }))}
                    className="w-16 border border-sky-200 rounded-md px-2 py-1 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400"
                    data-testid="tcs-rate-input"
                  />
                  <span>%</span>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 h-fit" data-testid="totals-preview">
            <div className="text-xs font-semibold text-gray-800 uppercase tracking-widest mb-3">Live totals</div>

            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Internal Breakdown</div>
              <div className="flex justify-between text-sm py-0.5"><span className="text-gray-500">Package Cost</span><span className="font-medium tabular-nums">{money(totals?.subtotal)}</span></div>
              <div className="flex justify-between text-sm py-0.5"><span className="text-gray-500">+ Commission</span><span className="font-medium tabular-nums">{money(totals?.commission)}</span></div>
              <div className="flex justify-between text-sm py-0.5"><span className="text-gray-500">+ 18% GST on Commission</span><span className="font-medium tabular-nums">{money(totals?.commission_gst)}</span></div>
            </div>

            <div className="border-t border-gray-100 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-[#0d64a8] mb-1">Customer View</div>
              <div className="flex justify-between text-sm py-0.5" data-testid="preview-cost-of-package"><span className="text-gray-700">Cost of Package</span><span className="font-semibold tabular-nums">{money(totals?.customer_package_cost)}</span></div>
              {totals?.tcs_amount > 0 && (
                <div className="flex justify-between text-sm py-0.5" data-testid="preview-tcs"><span className="text-gray-700">TCS @ {totals?.tcs_rate}% (International)</span><span className="font-semibold tabular-nums">{money(totals.tcs_amount)}</span></div>
              )}
              <div className="border-t border-gray-100 mt-2 pt-2 flex justify-between text-base font-bold text-[#0d64a8]" data-testid="preview-grand-total"><span>Grand Total</span><span className="tabular-nums">{money(totals?.grand_total)}</span></div>
            </div>

            <p className="text-[10px] text-gray-400 mt-3 italic">Server recomputes on save. Customer PDF shows just Cost of Package + TCS + Grand Total.</p>
          </div>
        </div>

        {/* Payments — only when editing an existing invoice (needs invoice ID) */}
        {editing !== 'new' && form._id && (
          <PaymentsBlock
            form={form}
            addPayment={addPayment}
            removePayment={removePayment}
            refresh={refreshCurrent}
          />
        )}
      </div>
    );
  }

  // ------------------ LIST VIEW ------------------
  const filteredInvoices = invoices.filter((inv) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    const hay = [inv.invoice_number, inv.customer?.name, inv.customer?.phone, inv.customer?.email, inv.customer?.state, inv.status]
      .filter(Boolean).join(' ').toLowerCase();
    return hay.includes(s);
  });

  return (
    <div data-testid="invoices-manager">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Invoices</h2>
          <p className="text-sm text-gray-500">Generate India-formatted invoices, download PDFs, and share via email or WhatsApp with one click.</p>
        </div>
        <button onClick={startNew} data-testid="invoice-new-btn" className="flex items-center gap-1.5 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-4 py-2 rounded-md"><Plus size={14}/> New Invoice</button>
      </div>

      {invoices.length > 0 && (
        <div className="mb-4">
          <AdminSearch value={q} onChange={setQ} placeholder="Search invoice #, customer name, phone, email, state..." testId="invoice-search" />
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-sm text-gray-500">Loading…</div>
      ) : invoices.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl py-14 text-center">
          <FileText className="mx-auto text-gray-300 mb-3" size={48}/>
          <div className="text-gray-800 font-semibold">No invoices yet</div>
          <p className="text-xs text-gray-500 mt-1">Click <strong>New Invoice</strong> to bill your first customer.</p>
          <button onClick={startNew} className="mt-4 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-5 py-2 rounded-md">Create your first invoice</button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-[11px] uppercase tracking-wider">
                <th className="px-4 py-2.5 text-left">Invoice #</th>
                <th className="px-4 py-2.5 text-left">Date</th>
                <th className="px-4 py-2.5 text-left">Customer</th>
                <th className="px-4 py-2.5 text-right">Amount</th>
                <th className="px-4 py-2.5 text-center">Status</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-4 py-3 font-mono text-xs text-[#0d64a8] font-semibold">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{fmtDate(inv.invoice_date)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{inv.customer?.name || '—'}</div>
                    <div className="text-[11px] text-gray-500">{inv.customer?.phone || ''} {inv.customer?.state && `· ${inv.customer.state}`}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">{money(inv.grand_total)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-widest border ${
                        inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        inv.status === 'sent' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        inv.status === 'cancelled' ? 'bg-gray-100 text-gray-500 border-gray-200' :
                        'bg-amber-50 text-amber-700 border-amber-200'
                      }`}>{inv.status || 'draft'}</span>
                      {(() => {
                        const paid = Number(inv.total_paid || 0);
                        const total = Number(inv.grand_total || 0);
                        const bal = Math.max(total - paid, 0);
                        // Payment status per spec: Fully Paid (green) / Unpaid (red) / Balance Due (red)
                        const isFullyPaid = total > 0 && paid >= total;
                        const isUnpaid = paid <= 0;
                        const meta = isFullyPaid
                          ? { label: 'Fully Paid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
                          : isUnpaid
                            ? { label: 'Unpaid', cls: 'bg-rose-50 text-rose-700 border-rose-200' }
                            : { label: `Balance Due: ₹${bal.toLocaleString('en-IN')}`, cls: 'bg-rose-50 text-rose-700 border-rose-200' };
                        // 15-day travel window warning: only when not fully paid AND
                        // a travel start date is available on the invoice.
                        let dueSoon = false;
                        const ts = (inv.travel_start_date || '').slice(0, 10);
                        if (!isFullyPaid && ts) {
                          const t = new Date(ts + 'T00:00:00');
                          const now = new Date();
                          now.setHours(0, 0, 0, 0);
                          const diffDays = Math.round((t - now) / 86400000);
                          if (diffDays >= 0 && diffDays <= 15) dueSoon = true;
                        }
                        return (
                          <>
                            <span data-testid={`inv-paystatus-${inv.id}`} className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${meta.cls}`}>{meta.label}</span>
                            {dueSoon && (
                              <span data-testid={`inv-due-warning-${inv.id}`} className="inline-block max-w-[200px] px-2 py-0.5 rounded text-[9px] font-medium border bg-rose-50 text-rose-700 border-rose-300 leading-tight">
                                Full payment due — travel date is within 15 days.
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => downloadPdf(inv, 'customer')} title="Download Customer copy (no GST breakup)" data-testid={`inv-pdf-${inv.id}`} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]"><Download size={13}/></button>
                      <button onClick={() => downloadPdf(inv, 'accountant')} title="Download Accountant copy (with GST breakup)" data-testid={`inv-pdf-gst-${inv.id}`} className="h-8 px-2 rounded-md border border-amber-200 flex items-center justify-center text-amber-700 hover:bg-amber-50 text-[10px] font-semibold tracking-widest">GST</button>
                      <button onClick={() => shareEmail(inv)} title="Send via Email (opens mail client)" data-testid={`inv-email-${inv.id}`} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]"><Send size={13}/></button>
                      <button onClick={() => shareWhatsApp(inv)} title="Send via WhatsApp" data-testid={`inv-wa-${inv.id}`} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-[#25D366] hover:bg-[#25D366]/10"><MessageCircle size={13}/></button>
                      <button onClick={() => copyPublicLink(inv)} title="Copy shareable public link" data-testid={`inv-link-${inv.id}`} className={`w-8 h-8 rounded-md border flex items-center justify-center transition ${copiedFor === inv.id ? 'border-green-300 text-green-600 bg-green-50' : 'border-gray-200 text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]'}`}>
                        {copiedFor === inv.id ? <Check size={13}/> : <Link2 size={13}/>}
                      </button>
                      <button onClick={() => duplicate(inv)} title="Duplicate as new draft" data-testid={`inv-dup-${inv.id}`} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]"><Copy size={13}/></button>
                      <button onClick={() => startEdit(inv)} title="Edit" data-testid={`inv-edit-${inv.id}`} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]"><FileText size={13}/></button>
                      <button onClick={() => remove(inv)} title="Delete" data-testid={`inv-delete-${inv.id}`} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:border-red-400 hover:text-red-500"><Trash2 size={13}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoicesManager;
